import { ReactNode, useRef } from 'react';
import { AriaButtonProps, mergeProps, useButton, useFocusRing } from 'react-aria';

import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { validateDataCy } from '~/components/scales/utils/data-cy';

type Props = TooltipTriggerProps & {
  /**
   * @deprecated Avoid relying on data-cy for tests. Prefer accessible queries:
   * `screen.getByRole('button', { name: 'aria-label' })`
   */
  'data-cy'?: string;
  endIcon?: ReactNode;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  label: string;
  onClick?: AriaButtonProps['onPress'];
  startIcon?: ReactNode;
};

/**
 * @deprecated TertiaryButton is deprecated. Using a button styled as a link
 * creates confusion about its behavior. If you think you need a TertiaryButton,
 * please consult with your designer to explore alternative solutions.
 */
export default function TertiaryButton(props: Props) {
  const ref = useRef(null);

  // Manage focus via useFocusRing since focus has a habit of appearing when clicking via
  // the mouse when we rely solely on useButton.
  const { isFocusVisible, focusProps } = useFocusRing();

  const { buttonProps } = useButton(
    {
      isDisabled: Boolean(props.isDisabled),
      onPress: props.onClick,
    },
    ref
  );

  const classes = [
    'group flex relative shrink-0 grow-0 type-button justify-center items-center gap-1 border-2 h-10 transition',
    props.isFullWidth ? 'w-full' : 'w-min whitespace-nowrap',
    'text-type-link border-transparent outline-none rounded',
    isFocusVisible ? 'bg-selection-hover' : 'bg-transparent',
    'disabled:text-type-inactive',
  ];

  let paddingClass = 'px-4';
  if (props.startIcon && props.endIcon) {
    paddingClass = 'px-2';
  } else if (props.startIcon) {
    paddingClass = 'pl-2 pr-4';
  } else if (props.endIcon) {
    paddingClass = 'pl-4 pr-2';
  }

  validateDataCy(props['data-cy'], 'button');

  return (
    <button
      {...mergeProps(pickTooltipTriggerProps(props), buttonProps, focusProps)}
      ref={ref}
      className={[...classes, paddingClass].join(' ')}
      data-cy={props['data-cy']}
    >
      {props.startIcon}
      <span className="group-hover:underline">{props.label}</span>
      {props.endIcon}
    </button>
  );
}
